import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Check, Command } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

export const OperatingSystem = () => {
  const [lines, setLines] = useState([]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    api.get("/operating-system").then((r) => setLines(r.data.lines)).catch((err) => console.error("OS load failed:", err));
  }, []);

  const save = async () => {
    const next = draft.split("\n").map((l) => l.trim()).filter(Boolean);
    try {
      const { data } = await api.put("/operating-system", { lines: next });
      setLines(data.lines);
      setEditing(false);
      toast.success("Operating system updated");
    } catch { toast.error("Couldn't save"); }
  };

  return (
    <section className="max-w-7xl mx-auto px-6 pb-24" data-testid="os-section">
      <div className="border border-white/10 rounded-md bg-[#0F0F11] p-8">
        <div className="flex items-center gap-3 mb-2">
          <Command className="w-5 h-5 text-[#D4AF37]" />
          <h3 className="font-display text-2xl text-white" data-testid="os-heading">My Operating System</h3>
          {editing ? (
            <button onClick={save} className="ml-auto inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] px-4 py-2 bg-emerald-500 text-black rounded-sm hover:bg-emerald-400 transition-colors duration-200" data-testid="os-save">
              <Check className="w-3.5 h-3.5" /> Save
            </button>
          ) : (
            <button onClick={() => { setEditing(true); setDraft(lines.join("\n")); }} className="ml-auto text-zinc-600 hover:text-[#D4AF37] transition-colors duration-200" data-testid="os-edit">
              <Pencil className="w-4 h-4" />
            </button>
          )}
        </div>
        <p className="text-xs text-zinc-500 mb-8">Short prompts to yourself. The code you run on — read it daily.</p>

        {editing ? (
          <Textarea
            value={draft} onChange={(e) => setDraft(e.target.value)} rows={8} autoFocus
            placeholder="One statement per line…"
            className="bg-white/5 border-white/10 text-white rounded-sm text-sm leading-relaxed focus-visible:ring-[#D4AF37]"
            data-testid="os-input"
          />
        ) : lines.length === 0 ? (
          <p className="text-sm text-zinc-600 italic" data-testid="os-empty">Write the code you run on…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
            {lines.map((line, i) => (
              <motion.div
                key={`${i}-${line}`} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.06 * i }}
                className="flex items-baseline gap-4 border-b border-white/5 pb-3"
                data-testid={`os-line-${i}`}
              >
                <span className="text-[10px] text-[#D4AF37] font-bold tracking-widest shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <p className="font-display text-lg text-zinc-100 leading-snug">{line}</p>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
